import { MessageSquare, Users, AlertTriangle } from 'lucide-react';
import { useAuth } from '@aaas/shared';
import { useWorkspaceSlackIntegrationByIdQuery } from '../hooks/useIntegrationQuery';
import SlackIntegrationCard from '../components/SlackIntegrationCard';
import SlackDisconnectCard from '../components/SlackDisconnectCard';
import { AccessMatrix } from '../components/AccessMatrix';
import { useParams, useNavigate } from 'react-router-dom';

const SlackIntegrationDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();

  // Fetch single Slack integration by ID
  const {
    data: slackIntegration,
    isLoading,
    error,
  } = useWorkspaceSlackIntegrationByIdQuery({
    integrationId: id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!idToken && !!id,
  });

  if (error) {
    return (
      <div className="space-y-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="font-semibold mb-2">Failed to load Slack integration</p>
          <p className="text-sm">Error: {error instanceof Error ? error.message : String(error)}</p>
          <p className="text-sm mt-2">Integration ID: {id}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">
          {slackIntegration ? slackIntegration.team_name : 'Slack Integration'}
        </h1>
        <p className="text-gray-600">Manage workspace connection and user access permissions</p>
      </div>

      {/* Workspace Integration Section */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Workspace Integration
        </h2>

        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <SlackIntegrationCard integration={slackIntegration || null} />
        )}
      </div>

      {/* User Access Management Section */}
      {slackIntegration && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Users className="w-5 h-5" />
            User Access Management
          </h2>
          <AccessMatrix integrationId={slackIntegration.id} />
        </div>
      )}

      {/* Disconnect Section */}
      {slackIntegration && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Disconnect Integration
          </h2>
          <SlackDisconnectCard
            integration={slackIntegration}
            onDisconnectSuccess={() => navigate('/kms/integrations')}
          />
        </div>
      )}
    </div>
  );
};

export default SlackIntegrationDetailPage;
